import { useState, useRef, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import ReactionPicker, { ReactionType } from './ReactionPicker';
import ReactionsModal from './ReactionsModal';
import { usePostReactions } from '../hooks/useReactions';

interface ReactionButtonProps {
  postId: string;
  showModal?: boolean;
  className?: string;
}

import likeEmoji from '../assets/like-emoji.png';
import loveEmoji from '../assets/love-emoji.png';
import careEmoji from '../assets/care-emoji.png';
import hahaEmoji from '../assets/hilarious-emoji.png';
import wowEmoji from '../assets/wow-emoji.png';
import sadEmoji from '../assets/sad-emoji.png';

const getReactionDisplay = (type: ReactionType | null) => {
  switch (type) {
    case 'like':
      return { color: 'text-blue-500', customImage: likeEmoji };
    case 'love':
      return { color: 'text-red-500', customImage: loveEmoji };
    case 'care':
      return { color: 'text-yellow-500', customImage: careEmoji };
    case 'haha':
      return { color: 'text-yellow-500', customImage: hahaEmoji };
    case 'wow':
      return { color: 'text-orange-500', customImage: wowEmoji };
    case 'sad':
      return { color: 'text-blue-400', customImage: sadEmoji };
    default:
      return { icon: Heart, color: 'text-gray-600' };
  }
};

const getTopReactionEmojis = (reactionCounts: any[], maxCount = 3) => {
  if (!reactionCounts || reactionCounts.length === 0) return [];
  
  return reactionCounts
    .filter(reaction => reaction.count > 0)
    .slice(0, maxCount)
    .map(reaction => {
      const display = getReactionDisplay(reaction.reaction_type);
      return {
        type: reaction.reaction_type,
        count: reaction.count,
        image: display.customImage
      };
    });
};

const ReactionButton = ({ postId, showModal = false, className }: ReactionButtonProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const { 
    data: postReactions, 
    addReaction, 
    removeReaction, 
    isLoading 
  } = usePostReactions(postId);

  const currentReaction = postReactions?.user_reaction || null;
  const totalCount = postReactions?.total_count || 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [showPicker, longPressTimer]);

  const handleQuickReaction = async () => {
    if (isLoading) return;

    try {
      if (currentReaction) {
        await removeReaction();
      } else {
        await addReaction('like');
      }
    } catch (error) {
      console.error('🔄 ReactionButton - Quick reaction failed:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Something went wrong",
        variant: "destructive",
      });
    }
  };

  const handleReactionSelect = async (reaction: ReactionType) => {
    if (isLoading) return;

    try {
      if (currentReaction === reaction) {
        await removeReaction();
      } else {
        await addReaction(reaction);
      }
      setShowPicker(false);
    } catch (error) {
      console.error('🔄 ReactionButton - Reaction selection failed:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Something went wrong",
        variant: "destructive",
      });
    }
  };

  const handleMouseDown = () => {
    const timer = setTimeout(() => {
      setShowPicker(true);
      setLongPressTimer(null);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
      
      if (!showPicker) {
        handleQuickReaction();
      }
    }
  };

  const handleMouseEnter = () => {
    if (!('ontouchstart' in window)) {
      const timer = setTimeout(() => {
        setShowPicker(true);
      }, 300);
      setLongPressTimer(timer);
    }
  };

  const handleMouseLeave = () => {
    if (longPressTimer && !('ontouchstart' in window)) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleCountClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (showModal && totalCount > 0) {
      setIsModalOpen(true);
    }
  };

  const display = getReactionDisplay(currentReaction);
  const topReactions = getTopReactionEmojis(postReactions?.reaction_counts || [], 3);

  return (
    <>
      <div 
        ref={buttonRef}
        className={cn("relative flex items-center gap-1", className)}
      >
        <div className="relative">
          <button
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            disabled={isLoading}
            className={cn(
              "flex items-center gap-1 p-1 rounded transition-all duration-150",
              "hover:bg-gray-100 dark:hover:bg-gray-800",
              currentReaction ? display.color : 'text-gray-600 hover:text-gray-800',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {topReactions.length > 0 ? (
              <div className="flex items-center -space-x-1">
                {topReactions.map((reaction, index) => (
                  <img 
                    key={reaction.type}
                    src={reaction.image} 
                    alt={reaction.type}
                    className={cn(
                      "w-5 h-5 object-contain rounded-full bg-white ring-1 ring-white",
                      "transition-all duration-150"
                    )}
                    style={{ zIndex: topReactions.length - index }}
                  />
                ))}
              </div>
            ) : (
              display.icon && (
                <display.icon 
                  className={cn(
                    "h-6 w-6 transition-all",
                    isLoading && 'animate-pulse'
                  )} 
                />
              )
            )}
          </button>

          <ReactionPicker
            isVisible={showPicker}
            onReactionSelect={handleReactionSelect}
            currentReaction={currentReaction}
            className="z-50"
          />
        </div>

        <button
          onClick={handleCountClick}
          className={cn(
            "text-sm transition-colors",
            showModal && totalCount > 0 
              ? 'text-gray-600 hover:text-gray-800 hover:underline cursor-pointer' 
              : 'text-gray-600 cursor-default'
          )}
        >
          {totalCount}
        </button>
      </div>

      {showModal && postReactions && (
        <ReactionsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          postId={postId}
          totalCount={postReactions.total_count}
          reactionCounts={postReactions.reaction_counts}
        />
      )}
    </>
  );
};

export default ReactionButton;